import AdminLayout from "../../commponents/layout/adminLayout";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { TableColumn } from "../../commponents/table/types";
import { Eye, Pencil, Trash2 } from "lucide-react";

import {
  createUser,
  deleteUserById,
  editUser,
  getUsers,
  getUserById,
} from "../../api/userApi";
import Modal from "../../commponents/Modal";
import DataTable from "../../commponents/table/DataTable";
import { showError } from "../../utils/ alert";

interface User {
  id: number;
  name: string;
  username: string;
  phone: string;
  email: string;
  role: string;
  status: string;
}

interface UserForm {
  name: string;
  username: string;
  phone: string;
  email: string;
  password: string;
  role: string;
}

// Sesuai enum role di backend (users_tb.ts). Figma menampilkan "Super Admin"
// tapi itu belum ada di database -- kalau memang dibutuhkan, tambahkan dulu
// value-nya ke ENUM role di model + migration sebelum ditambahkan di sini.
const ROLE_OPTIONS = ["Admin", "Staff", "Sales"];

export default function UserList() {
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [users, setUsers] = useState<User[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalData, setTotalData] = useState(0);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [search, setSearch] = useState("");

  const [isEdit, setIsEdit] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const initialForm: UserForm = {
    name: "",
    username: "",
    phone: "",
    email: "",
    password: "",
    role: "Staff",
  };

  const [form, setForm] = useState<UserForm>(initialForm);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const columns: TableColumn<User>[] = [
    {
      key: "name",
      title: "Nama",
    },
    {
      key: "phone",
      title: "No. Telp",
    },
    {
      key: "email",
      title: "Email",
    },
    {
      key: "role",
      title: "Level",
    },
    {
      key: "id",
      title: "Action",
      render: (row) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/User/Detail/${row.id}`)}
            className="rounded-lg bg-blue-100 p-2 text-blue-600 hover:bg-blue-200">
            <Eye size={18} />
          </button>

          <button
            onClick={() => handleEdit(row.id)}
            className="rounded-lg bg-yellow-100 p-2 text-yellow-600 hover:bg-yellow-200">
            <Pencil size={18} />
          </button>

          <button
            onClick={() => handleDelete(row.id)}
            className="rounded-lg bg-red-100 p-2 text-red-600 hover:bg-red-200">
            <Trash2 size={18} />
          </button>
        </div>
      ),
    },
  ];

  const fetchUsers = async () => {
    try {
      setLoading(true);

      const response = await getUsers({
        page: currentPage,
        limit: pageSize,
        search,
      });

      setUsers(response.data.data.UserList);
      setTotalPages(response.data.data.pagination.totalPages);
      setTotalData(response.data.data.pagination.totalData);
    } catch (error) {
      showError(
        "Failed",
        "Failed to Load data."
    );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, search]);

  const handleSave = async () => {
    try {
      if (!form.name.trim()) {
        alert("User name is required");
        return;
      }

      if (!form.username.trim()) {
        alert("Username is required");
        return;
      }

      if (!isEdit && form.password.trim().length < 8) {
        alert("Password must be at least 8 characters");
        return;
      }

      if (isEdit && selectedId !== null) {
        await editUser(selectedId, {
          name: form.name,
          username: form.username,
          phone: form.phone,
          email: form.email,
          role: form.role,
          // password cuma dikirim kalau diisi -- kosongin berarti tidak
          // diganti (lihat fix edit/request.ts di backend)
          ...(form.password.trim() !== "" ? { password: form.password } : {}),
        });

        alert("User updated successfully.");
      } else {
        await createUser({
          name: form.name,
          username: form.username,
          phone: form.phone,
          email: form.email,
          password: form.password,
          role: form.role,
        });

        alert("User created successfully.");
      }

      await fetchUsers();

      handleCloseModal();
    } catch (error: any) {
      showError(
        "Failed",
        "Failed to Load data."
    );
      alert(error?.response?.data?.message ?? "Failed to save user.");
    }
  };

  const handleEdit = async (id: number) => {
    try {
      const { data } = await getUserById(id);
      const user = data.data;

      setForm({
        name: user.name,
        username: user.username,
        phone: user.phone ?? "",
        email: user.email,
        password: "",
        role: user.role,
      });

      setSelectedId(id);
      setIsEdit(true);
      setOpenModal(true);
    } catch (error) {
      showError(
        "Failed",
        "Failed to Load data."
    );
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const confirmed = window.confirm(
        "Are you sure you want to delete this user?"
      );

      if (!confirmed) return;

      await deleteUserById(id);

      await fetchUsers();

      alert("User deleted successfully.");
    } catch (error: any) {
      showError(
        "Failed",
        "Failed to Load data."
    );
      alert(error?.response?.data?.message ?? "Failed to delete user.");
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setForm(initialForm);
    setSelectedId(null);
    setIsEdit(false);
  };

  const handleAdd = () => {
    setIsEdit(false);
    setSelectedId(null);
    setForm(initialForm);
    setOpenModal(true);
  };

  return (
    <AdminLayout>
      <div className="bg-white rounded-3xl p-6">
        <DataTable
          title="User Management"
          columns={columns}
          data={users}
          onAdd={handleAdd}
          showSearch={false}
          showFilter={false}
          pagination={{
            currentPage,
            totalPages,
            pageSize,
            onPageChange: setCurrentPage,
            onPageSizeChange: (size) => {
              setPageSize(size);
              setCurrentPage(1);
            },
          }}
        />

        <Modal
          open={openModal}
          title={isEdit ? "Edit User" : "Add User"}
          onClose={handleCloseModal}>
          <div className="space-y-4">
            {/* Name */}
            <div>
              <label className="mb-2 block font-semibold">Nama</label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Enter name"
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Username */}
            <div>
              <label className="mb-2 block font-semibold">User Name</label>
              <input
                type="text"
                name="username"
                value={form.username}
                onChange={handleChange}
                placeholder="Enter username"
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="mb-2 block font-semibold">No. Telp</label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="Enter phone number"
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Email */}
            <div>
              <label className="mb-2 block font-semibold">Email</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="Enter email"
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Password */}
            <div>
              <label className="mb-2 block font-semibold">Password</label>
              <input
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                placeholder={
                  isEdit
                    ? "Leave blank to keep current password"
                    : "Enter password"
                }
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Level / Role */}
            <div>
              <label className="mb-2 block font-semibold">Level</label>
              <select
                name="role"
                value={form.role}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none">
                {ROLE_OPTIONS.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </div>

            {/* Action */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={handleCloseModal}
                className="rounded-lg bg-gray-500 px-5 py-2 text-white hover:bg-gray-600">
                Cancel
              </button>

              <button
                onClick={handleSave}
                className="rounded-lg bg-blue-600 px-5 py-2 text-white hover:bg-blue-700">
                {isEdit ? "Update" : "Save"}
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </AdminLayout>
  );
}
